// Radha — conversational deal capture. Ported mechanism from Nikunj's Capture:
// paste a free-text note, it drafts a deal for confirmation. The inference here
// is generic B2B (customer match + amount + stage keywords) — none of THB's
// pharma keyword models, POD logic, ledger catalog or account list.
import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

const STAGES = ["lead", "qualified", "proposal", "negotiation", "won", "lost"];
const STAGE_WP: Record<string, number> = {
  lead: 0.2, qualified: 0.35, proposal: 0.55, negotiation: 0.7, won: 1, lost: 0,
};

// ₹ amount: "40L" / "1.2Cr" / "₹5,00,000" / "500k"
function inferAmount(text: string): number {
  const m = text.match(/₹?\s*([\d][\d,.]*)\s*(cr(?:ore)?|l(?:akh|acs)?|k)?/i);
  if (!m) return 0;
  const n = Number(m[1].replace(/,/g, ""));
  if (Number.isNaN(n)) return 0;
  const u = (m[2] || "").toLowerCase();
  if (u.startsWith("cr")) return n * 1e7;
  if (u.startsWith("l")) return n * 1e5;
  if (u === "k") return n * 1e3;
  return n;
}

function inferStage(text: string): string {
  const t = text.toLowerCase();
  if (/\b(won|closed[- ]won|signed|awarded)\b/.test(t)) return "won";
  if (/\b(lost|declined|dropped|chose\b)/.test(t)) return "lost";
  if (/\b(negotiat|pricing|contract|terms|redlin)/.test(t)) return "negotiation";
  if (/\b(propos|quote|quotation|estimate)/.test(t)) return "proposal";
  if (/\b(qualif|budget|shortlist|demo|poc|pilot)/.test(t)) return "qualified";
  return "lead";
}

// customer: an existing account named in the text (token overlap), else a
// capitalised phrase after a cue word, else the first Capitalised run.
function inferCustomer(text: string, customers: string[]): string {
  const lower = text.toLowerCase();
  let best = "", bestScore = 0;
  for (const c of customers) {
    if (lower.includes(c.toLowerCase())) return c;
    const toks = c.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    const score = toks.filter((w) => lower.includes(w)).length;
    if (score > bestScore) { bestScore = score; best = c; }
  }
  if (bestScore > 0) return best;
  const cue = text.match(/(?:from|for|with|at|by)\s+([A-Z][\w&.]*(?:\s+[A-Z][\w&.]*){0,3})/);
  if (cue) return cue[1].trim();
  const cap = text.match(/[A-Z][\w&.]*(?:\s+[A-Z][\w&.]*){0,3}/);
  return cap ? cap[0].trim() : "New account";
}

interface Draft { deal_name: string; customer_name: string; stage: string; amount: number; }

export default function CaptureDeal({ customers }: { customers: string[] }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const uniqueCustomers = useMemo(() => [...new Set(customers)], [customers]);

  const parse = () => {
    setNote(null);
    const customer = inferCustomer(text, uniqueCustomers);
    const stage = inferStage(text);
    const amount = inferAmount(text);
    const words = text.trim().split(/\s+/).slice(0, 6).join(" ");
    setDraft({ deal_name: `${customer} — ${words}`.slice(0, 80), customer_name: customer, stage, amount });
  };

  const create = async () => {
    if (!draft) return;
    setBusy(true); setNote(null);
    try {
      const { error } = await supabase.from("crm_deals").insert({
        deal_name: draft.deal_name, customer_name: draft.customer_name,
        stage: draft.stage, amount: draft.amount, win_probability: STAGE_WP[draft.stage] ?? 0.2,
      });
      if (error) throw error;
      setNote({ ok: true, text: "Deal created" });
      setDraft(null); setText("");
      qc.invalidateQueries({ queryKey: ["crm-direct", "crm_deals"] });
      qc.invalidateQueries({ queryKey: ["crm-stage-rates"] });
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : "failed" });
    } finally { setBusy(false); }
  };

  const fld: React.CSSProperties = { padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13 };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "12px 14px", marginBottom: 22, background: "var(--surface-2)" }}>
      <div style={{ font: "600 13px Inter", marginBottom: 6 }}>Capture a deal</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="e.g. New enquiry from Acme Corp for a 40L annual plan, sent the proposal last week"
          style={{ ...fld, flex: 1, minWidth: 280, resize: "vertical", font: "13px Inter" }} />
        <button onClick={parse} disabled={!text.trim()}
          style={{ padding: "7px 14px", border: "1px solid var(--border)", background: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
          Draft
        </button>
      </div>

      {draft && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input value={draft.deal_name} onChange={(e) => setDraft({ ...draft, deal_name: e.target.value })} style={{ ...fld, minWidth: 220, flex: 1 }} />
          <input value={draft.customer_name} onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} style={{ ...fld, minWidth: 140 }} />
          <select value={draft.stage} onChange={(e) => setDraft({ ...draft, stage: e.target.value })} style={fld}>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="number" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) })} style={{ ...fld, width: 120 }} />
          <button onClick={create} disabled={busy}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {busy ? "Creating…" : "Create deal"}
          </button>
        </div>
      )}
      {note && <span style={{ display: "inline-block", marginTop: 8, fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
    </div>
  );
}
